'use client';

import { useCallback, useEffect, useRef, type ChangeEvent, type ForwardedRef, type KeyboardEvent, type RefObject } from 'react';
import type { Address, LoqateFindItem } from '../types';
import { useAddressLookup } from './UseAddressLookup';
import { useAddressLookupState } from './UseAddressLookupState';
import { useKeyboardNavigation } from './UseKeyboardNavigation';
import { useScrollManagement } from './UseScrollManagement';

/**
 * Props for the useAddressLookupController hook
 */
type UseAddressLookupControllerProps = {
  /** Ref to the input element (optional) */
  ref?: RefObject<HTMLInputElement> | ForwardedRef<HTMLInputElement>;
  /** Current input value */
  value: string;
  /** Callback when input value changes */
  onChange?: (value: string) => void;
  /** Callback when an address is selected */
  onAddressSelect?: (address: Address) => void;
  /** Callback when the suggestions popover open state changes */
  onOpenChange?: (open: boolean) => void;
};

/**
 * Return type for the useAddressLookupController hook
 */
type UseAddressLookupControllerReturn = {
  /** Ref to the input element */
  inputRef: RefObject<HTMLInputElement | null>;
  /** List of address suggestions */
  suggestions: LoqateFindItem[];
  /** Whether suggestions are currently loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether the suggestions popover is open */
  open: boolean;
  /** Index of the currently highlighted suggestion */
  highlightedIndex: number;
  /** Whether the suggestions list is scrollable */
  isScrollable: boolean;
  /** Ref to the suggestions container */
  suggestionsRef: RefObject<HTMLDivElement | null>;
  /** Handler for input change events */
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Handler for input keydown events */
  handleInputKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** Handler for suggestion item keydown events */
  handleItemKeyDown: (e: KeyboardEvent<Element>, suggestion: LoqateFindItem) => void;
  /** Handler for suggestion selection */
  handleSelect: (suggestion: LoqateFindItem) => void;
  /** Handler for mouse enter on suggestion items */
  handleItemMouseEnter: (index: number) => void;
  /** Handler for popover open state changes */
  handlePopoverOpenChange: (open: boolean) => void;
  /** Handler for focus events */
  handleFocus: () => void;
};

/**
 * Controller hook that centralizes all the logic for the AddressLookup component
 */
function useAddressLookupController({ ref, value, onChange, onAddressSelect, onOpenChange }: UseAddressLookupControllerProps): UseAddressLookupControllerReturn {
  // Get address lookup functionality from the hook
  const { suggestions, showSuggestions, setShowSuggestions, isLoading, error, inputRef, suggestionsRef, handleInputChange, handleSelectSuggestion, handleFocus } = useAddressLookup(
    {
      value,
      onChange,
      onAddressSelect,
    }
  );

  // Use the reducer-based state management
  const { open, highlightedIndex, isScrollable, setOpen, setHighlightedIndex, setIsScrollable } = useAddressLookupState();

  // Set up combined ref for the input
  const combinedRef = useRef<HTMLInputElement | null>(null);

  // Update the combined ref when dependencies change
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        // Handle function ref
        const current = inputRef.current;
        if (current) ref(current);
      } else {
        // Handle object ref
        combinedRef.current = ref.current;
      }
    } else {
      combinedRef.current = inputRef.current;
    }
  }, [ref, inputRef]);

  // Handle scroll management
  useScrollManagement({
    suggestionsRef,
    highlightedIndex,
    id: 'address-lookup',
    suggestions,
    open,
    setIsScrollable,
  });

  // Handle input change with highlighted index reset
  const handleEnhancedInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e);
    // Reset highlighted index when input changes
    setHighlightedIndex(-1);
  };

  // Handle suggestion selection
  const handleSelect = useCallback(
    (suggestion: LoqateFindItem) => {
      handleSelectSuggestion(suggestion);

      // Container results (for example a postcode match typed as Building) drill into another Find call.
      // Closing here unmounts the list before those results can be chosen.
      if (suggestion.Type === 'Address') {
        setOpen(false);
      }
      setHighlightedIndex(-1);

      // Return focus to input after selection
      if (combinedRef.current) {
        combinedRef.current.focus();
      }
    },
    [handleSelectSuggestion, setOpen, setHighlightedIndex]
  );

  // Handle mouse enter on suggestion items
  const handleItemMouseEnter = useCallback(
    (index: number) => {
      if (index !== highlightedIndex) {
        setHighlightedIndex(index);
      }
    },
    [highlightedIndex, setHighlightedIndex]
  );

  // Set up keyboard navigation
  const { handleInputKeyDown, handleItemKeyDown: handleKeyboardItemKeyDown } = useKeyboardNavigation({
    open,
    suggestions,
    highlightedIndex,
    inputRef: combinedRef,
    setHighlightedIndex,
    setOpen,
    onSelect: handleSelect,
  });

  // Adapt the item key down handler to match the expected type
  const handleItemKeyDown = useCallback(
    (e: KeyboardEvent<Element>, suggestion: LoqateFindItem) => {
      // Find the index of the suggestion
      const index = suggestions.findIndex(item => item.Id === suggestion.Id);

      // Call the keyboard navigation handler with the index
      if (index !== -1) {
        // We need to cast the event type here to match what useKeyboardNavigation expects
        const keyboardEvent = e as unknown as KeyboardEvent<HTMLElement>;
        // Pass the suggestion instead of the index to match the expected parameter type
        handleKeyboardItemKeyDown(keyboardEvent, suggestion);
      }
    },
    [suggestions, handleKeyboardItemKeyDown]
  );

  // Custom handler for Popover's onOpenChange
  const handlePopoverOpenChange = useCallback(
    (newOpen: boolean) => {
      // Only propagate an open state when we actually have suggestions to show
      if (newOpen === open) return;

      const effectiveOpen = newOpen && suggestions.length > 2;
      setOpen(effectiveOpen);

      // Reset scrollable when closing
      if (!effectiveOpen) {
        setIsScrollable(false);
      }

      if (typeof setShowSuggestions === 'function') {
        setShowSuggestions(effectiveOpen);
      }

      if (onOpenChange) {
        onOpenChange(effectiveOpen);
      }
    },
    [open, suggestions.length, setOpen, setIsScrollable, setShowSuggestions, onOpenChange]
  );

  // Single effect to sync state between showSuggestions and open
  useEffect(() => {
    const hasSuggestions = suggestions.length > 0;

    // Only update open state if showSuggestions is true and we have suggestions
    if (showSuggestions && hasSuggestions && !open) {
      setOpen(true);

      if (onOpenChange) {
        onOpenChange(true);
      }
    }
    // Close if showSuggestions is false or we have no suggestions
    else if ((!showSuggestions || !hasSuggestions) && open) {
      setOpen(false);

      // Ensure scrollable state resets on close
      setIsScrollable(false);

      if (onOpenChange) {
        onOpenChange(false);
      }
    }
  }, [showSuggestions, suggestions.length, open, setOpen, setIsScrollable, onOpenChange]);

  return {
    inputRef,
    suggestions,
    isLoading,
    error,
    open,
    highlightedIndex,
    isScrollable,
    suggestionsRef,
    handleInputChange: handleEnhancedInputChange,
    handleInputKeyDown,
    handleItemKeyDown,
    handleSelect,
    handleItemMouseEnter,
    handlePopoverOpenChange,
    handleFocus,
  };
}

// Export the hook at the end of the file
export { useAddressLookupController };
